import { useId } from 'react'

import { cx } from './cx.ts'
import { useDisabled, type DisabledState } from './disabled.tsx'

import './form.css'

/**
 * 입력 상자 — 와이어프레임 `box()` 1022행 · `.box` CSS 151행.
 *
 * 비활성 규약은 버튼과 같다: `disabled` 속성 대신 `aria-disabled` + `readOnly`.
 * 포커스를 살려야 사유를 읽을 수 있다(§11-4-2).
 *
 * 🔴 **「비활성」과 「읽기 전용」은 다른 것이다**(2026-09-21 신설 · eric 이 잡아낸 자리).
 *    비활성은 「지금은 못 고친다 — 조건이 풀리면 고칠 수 있다」라 흐리게 두고 사유를 단다.
 *    **읽기 전용은 애초에 고치는 칸이 아니다.** 확정·보관 레시피처럼 값이 불변이면
 *    비활성으로 흐리게 두면 안 된다 — 흐린 글자는 **placeholder(기본값 안내)로 읽힌다.**
 *    eric 원문: *「시약 부분 설정 부분이 희미한 색인데 이게 디폴트 값이야?」* — 그 값들은
 *    전부 그 레시피의 «실제 값»이었다. 그래서 `readOnly` 는 **진한 글자**로 둔다.
 */
export type TextInputProps = {
  /**
   * 제어 입력의 값. **`defaultValue` 와 같이 쓰지 마라** — 하나만 준다.
   *
   * 🔴 **브라우저 자동완성이 닿는 칸에는 제어를 쓰지 마라**(2026-09-23 실측). 제어 입력은
   *    **렌더할 때마다 React 가 DOM 의 `value` 를 제 값으로 되돌린다** — 크롬 autofill 은
   *    `onChange` 를 안 올리므로 상태가 빈 채로 남고, 그 뒤 **아무 재렌더(형제 스위치 토글 ·
   *    서버 오류 도착)에서 채워져 있던 글자가 지워진다.** 로그인 칸이 그 자리였다.
   */
  value?: string | undefined
  /**
   * 비제어 입력의 초기값. 🔴 **React 가 그 뒤로 DOM 을 건드리지 않는다** — 자동완성이 넣은
   *    값이 재렌더에도 살아남는다. 보낼 값은 폼의 `FormData`(또는 ref)로 읽는다.
   * ⚠ `value` 와 **동시에 주지 않는다.** 주면 제어가 이긴다(React 경고를 피하려 갈라 둔다).
   */
  defaultValue?: string | undefined
  onChange?: ((value: string) => void) | undefined
  /**
   * 기본 'text'. 숫자 자릿수 정렬이 필요하면 'number'.
   * ⚠ `datetime-local` 은 **브라우저 로컬 시간대**로 읽고 쓴다 — 이 리포의 표시 시간대
   *   (`DISPLAY_TIME_ZONE`)와 다를 수 있다. 값을 계약으로 옮길 때 반드시 변환한다
   *   (`screens/modules/calibrationDraft.ts`). 날것으로 서버에 보내지 마라.
   */
  type?: 'text' | 'number' | 'password' | 'datetime-local' | 'date' | undefined
  /*
   * ⚠ `list`(브라우저 `<datalist>`)를 두지 않는다 — 브라우저가 그리는 목록이라 앱 드롭다운과 모양이
   *   따로 논다(eric 2026-09-29 「다른 메뉴의 내용이랑 다른데」). 고르거나 적는 칸은 `Combobox` 다.
   */
  /** 화상 키보드 종류. 숫자 칸을 `type='number'` 로 두지 않고 글자로 받을 때 `decimal` */
  inputMode?: 'text' | 'decimal' | 'numeric' | undefined
  /** 넓은 왼쪽 정렬 입력 */
  wide?: boolean | undefined
  /**
   * 고칠 수 없는 «값»이다. 🔴 `disabled` 와 **같이 쓰지 마라** — 이것이 이긴다.
   * 흐려지지 않고, 사유도 붙지 않는다(고칠 수 없는 것이 조건이 아니라 성질이라서).
   */
  readOnly?: boolean | undefined
  /** 규칙을 위반한 상태. 테두리가 굵어지고 `--color-danger` 가 얹힌다(콘솔은 붉다) — 칸 아래 문구가 같이 말한다 */
  invalid?: boolean | undefined
  /** 위반 문구 id. `Field` 의 error 와 묶는다 */
  describedBy?: string | undefined
  id?: string | undefined
  name?: string | undefined
  placeholder?: string | undefined
  autoComplete?: string | undefined
  'aria-label'?: string | undefined
} & DisabledState

export function TextInput({
  value,
  defaultValue,
  onChange,
  type = 'text',
  wide,
  invalid,
  describedBy,
  id,
  name,
  placeholder,
  autoComplete,
  'aria-label': ariaLabel,
  inputMode,
  readOnly,
  disabled,
  disabledReason,
}: TextInputProps) {
  const fallbackId = useId()
  // 🔴 읽기 전용이 이긴다 — 비활성 표시(흐림 · 사유)를 아예 만들지 않는다
  const { isDisabled, ariaProps, reasonNode } = useDisabled(
    readOnly === true ? false : disabled,
    disabledReason,
  )
  const inputId = id ?? fallbackId
  const isReadOnly = readOnly === true

  // 사유와 규칙 위반 문구가 둘 다 있으면 둘 다 읽혀야 한다
  const describedByIds = [ariaProps['aria-describedby'], describedBy].filter(
    (v): v is string => typeof v === 'string',
  )

  return (
    <>
      <input
        aria-disabled={ariaProps['aria-disabled']}
        aria-invalid={invalid}
        aria-label={ariaLabel}
        aria-readonly={isReadOnly ? true : undefined}
        autoComplete={autoComplete}
        className={cx(
          'a-input',
          wide === true && 'a-input--wide',
          isReadOnly && 'a-input--readonly',
        )}
        id={inputId}
        inputMode={inputMode}
        name={name}
        onChange={isDisabled || isReadOnly ? undefined : (e) => onChange?.(e.target.value)}
        placeholder={placeholder}
        readOnly={isDisabled || isReadOnly}
        title={ariaProps.title}
        type={type}
        {...(value === undefined ? { defaultValue } : { value })}
        {...(describedByIds.length > 0 ? { 'aria-describedby': describedByIds.join(' ') } : {})}
      />
      {reasonNode}
    </>
  )
}
